/**
 * Store de Tareas Rápidas - Zustand
 *
 * Maneja tareas temporales que:
 * - Se agregan al bloque horario actual
 * - Desaparecen al día siguiente
 * - Sin subtareas ni configuración compleja
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { QuickTask, TimeBlock } from '@/core/types';
import { getNowISO } from '@/core/utils';

interface QuickTaskState {
  // === State ===
  quickTasks: QuickTask[];

  // === Actions ===
  addQuickTask: (label: string, timeBlock: TimeBlock) => string;
  toggleQuickTask: (id: string) => void;
  deleteQuickTask: (id: string) => void;
  cleanupOldTasks: () => void;

  // === Selectors ===
  getTasksByTimeBlock: (timeBlock: TimeBlock) => QuickTask[];
  getTodayTasks: () => QuickTask[];
}

/**
 * Genera un ID único para tarea rápida
 */
const generateQuickTaskId = () => `qt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/**
 * Obtiene la fecha de hoy en formato YYYY-MM-DD
 */
const getTodayDate = () => {
  const now = new Date();
  return now.toISOString().split('T')[0] ?? now.toISOString().slice(0, 10);
};

export const useQuickTaskStore = create<QuickTaskState>()(
  persist(
    immer((set, get) => ({
      // === Initial State ===
      quickTasks: [],

      // === Actions ===
      addQuickTask: (label, timeBlock) => {
        const id = generateQuickTaskId();
        const now = getNowISO();
        const today = getTodayDate();

        const newTask: QuickTask = {
          id,
          label: label.trim(),
          timeBlock,
          isDone: false,
          createdDate: today,
          createdAt: now,
        };

        set((state) => {
          state.quickTasks.push(newTask);
        });

        return id;
      },

      toggleQuickTask: (id) => {
        set((state) => {
          const task = state.quickTasks.find((t) => t.id === id);
          if (task) {
            task.isDone = !task.isDone;
          }
        });
      },

      deleteQuickTask: (id) => {
        set((state) => {
          const index = state.quickTasks.findIndex((t) => t.id === id);
          if (index !== -1) {
            state.quickTasks.splice(index, 1);
          }
        });
      },

      cleanupOldTasks: () => {
        const today = getTodayDate();
        set((state) => {
          // Eliminar tareas de días anteriores
          state.quickTasks = state.quickTasks.filter((t) => t.createdDate === today);
        });
      },

      // === Selectors ===
      getTasksByTimeBlock: (timeBlock) => {
        const today = getTodayDate();
        return get().quickTasks.filter(
          (t) => t.timeBlock === timeBlock && t.createdDate === today
        );
      },

      getTodayTasks: () => {
        const today = getTodayDate();
        return get().quickTasks.filter((t) => t.createdDate === today);
      },
    })),
    {
      name: 'exocortex-quick-tasks',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        quickTasks: state.quickTasks,
      }),
    }
  )
);

// === Selectores externos ===

/**
 * Selector para tareas de un bloque específico (solo hoy)
 */
export const selectQuickTasksByTimeBlock = (timeBlock: TimeBlock) => (state: QuickTaskState) => {
  const today = getTodayDate();
  return state.quickTasks.filter(
    (t) => t.timeBlock === timeBlock && t.createdDate === today
  );
};

/**
 * Selector para contar tareas pendientes de hoy
 */
export const selectPendingCount = (state: QuickTaskState) => {
  const today = getTodayDate();
  return state.quickTasks.filter((t) => t.createdDate === today && !t.isDone).length;
};
